import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { AltaDosPasos, CodigosRecuperacion, Cuenta as DatosCuenta, FormasDeEntrar, SesionAbierta } from '../../api/ops/cuenta';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { CodigoQr } from '../../componentes/CodigoQr';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { emailValido } from '../../logica/acceso';
import { haceCuanto } from '../../logica/tiempo';
import { useCabecera } from '../../marco/cabecera';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { Fila } from './Fila';
import { APARTADOS, AYUDA, ERRORES, IDIOMAS, METODO, PIE, PROVEEDOR, dispositivo, esMovil, fechaLarga, hora, unir, type Apartado } from './textos';
import './cuenta.css';

/**
 * Mi cuenta y seguridad (1.8): Perfil · Formas de entrar · Seguridad · Sesiones · PIN de tablet, contra Identidad y Acceso.
 * Es de la cuenta, no del negocio: el modo solo lectura del negocio no la cambia (todas sus operaciones lo permiten).
 *
 * - Perfil: nombre (`updateMe`), email con el cambio pendiente (G172: `startEmailChange`, «Reenviar» con
 *   `sendEmailVerification`, G211, y «Anular» con `cancelEmailChange`, G233), idioma (`updateMe`) y negocios.
 * - Formas de entrar: contraseña («Cambiada el…», G179; cambiar o crear) y los proveedores que dé `listMySignInMethods`.
 *   Vincular y quitar Google o Apple esperan a que el back los tenga (ADR-0043): no hay botones que fallarían.
 * - Seguridad: activar (enlace `otpauth://`, «Ver la clave» y código), quitar los dos pasos y generar códigos nuevos
 *   («Quedan N…», G179). Tras crear la contraseña se ofrecen los dos pasos con «Ahora no» (G117).
 * - Sesiones: cada una con cómo se abrió (`sign_in_method`), cerrar una o todas las demás con confirmación (G176).
 * - PIN de este negocio: elegido, sin elegir o bloqueado hasta una hora (G174, G234); elegir uno nuevo lo desbloquea.
 */
type Modal = 'nombre' | 'email' | 'contrasena' | 'crearContrasena' | 'ofrecer' | 'activar' | 'quitar2' | 'regenerar' | 'codigos' | 'pin' | 'cerrarDemas';

interface DefVentana {
    titulo: string;
    texto?: string;
    accion: string;
    icono: NombreIcono;
    cancelar?: string;
    onAccion: () => void;
    cuerpo?: ReactNode;
}

const esApartado = (v: string | null): v is Apartado => APARTADOS.some((a) => a.clave === v);

export function Cuenta({ ops, negocio, yo, membresia, nombreNegocio, movil, ruta, busqueda, navegar, recargar }: PropsPantalla) {
    const avisar = useAvisoBreve();
    const pedido = busqueda.get('apartado');
    // Las cuentas de plataforma no tienen PIN de tablet; el resto, solo siendo miembro de este negocio.
    const apartados = APARTADOS.filter((a) => a.clave !== 'pin' || (membresia !== undefined && yo.account.type !== 'platform'));
    const apartado: Apartado = esApartado(pedido) && apartados.some((a) => a.clave === pedido) ? pedido : 'perfil';
    useCabecera({ ayuda: AYUDA[apartado], acciones: [] });

    const [cuenta, setCuenta] = useState<DatosCuenta>(yo.account);
    const [membresias, setMembresias] = useState(yo.memberships);
    const [formas, setFormas] = useState<FormasDeEntrar | null>(null);
    const [sesiones, setSesiones] = useState<SesionAbierta[] | null>(null);
    const [modal, setModal] = useState<Modal | null>(null);
    const [f, setF] = useState<Record<string, string>>({});
    const [intentado, setIntentado] = useState(false);
    const [errorCampo, setErrorCampo] = useState<Record<string, string>>({});
    const [ocupado, setOcupado] = useState(false);
    const [alta, setAlta] = useState<AltaDosPasos | null>(null);
    const [verClave, setVerClave] = useState(false);
    const [codigos, setCodigos] = useState<CodigosRecuperacion['recovery_codes']>([]);

    const fallo = useCallback(
        (e: unknown) => {
            avisar({ tipo: 'error', texto: e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : ERRORES.guardar });
        },
        [avisar],
    );

    const releer = useCallback(() => {
        ops.getMe()
            .then((r) => {
                setCuenta(r.account);
                setMembresias(r.memberships);
            })
            .catch(() => undefined);
    }, [ops]);

    useEffect(() => {
        if (apartado === 'entrar' || apartado === 'seguridad') {
            ops.listMySignInMethods().then(setFormas).catch(fallo);
        }
        if (apartado === 'sesiones') {
            ops.listMySessions()
                .then((l) => {
                    setSesiones(l.data);
                })
                .catch(fallo);
        }
    }, [apartado, ops, fallo]);

    const abrir = (m: Modal, valores: Record<string, string> = {}) => {
        setModal(m);
        setF(valores);
        setIntentado(false);
        setErrorCampo({});
        setVerClave(false);
    };
    const cerrar = () => {
        setModal(null);
        setOcupado(false);
    };
    const campo = (k: string) => f[k] ?? '';
    const pon = (k: string, filtro?: (v: string) => string) => (v: string) => {
        setF((x) => ({ ...x, [k]: filtro === undefined ? v : filtro(v) }));
    };
    const cifras = (max: number) => (v: string) => v.replace(/\D/g, '').slice(0, max);

    const errores: Record<string, string> = {
        nombre: campo('nombre').trim() === '' ? ERRORES.nombre : '',
        email: !emailValido(campo('email')) ? ERRORES.email : campo('email').trim().toLowerCase() === cuenta.email.toLowerCase() ? ERRORES.mismoEmail : '',
        actual: campo('actual') === '' ? ERRORES.actual : '',
        nueva: campo('nueva').length < 12 ? ERRORES.minimo : '',
        repetida: campo('repetida') === campo('nueva') ? '' : ERRORES.noCoinciden,
        codigo: /^\d{6}$/.test(campo('codigo')) ? '' : ERRORES.codigo,
        pin: /^\d{4,6}$/.test(campo('pin')) ? '' : ERRORES.pin,
        pin2: campo('pin2') === campo('pin') ? '' : ERRORES.noCoinciden,
    };
    const err = (k: string): string => {
        const deApi = errorCampo[k] ?? '';
        return deApi !== '' ? deApi : intentado ? (errores[k] ?? '') : '';
    };

    /** Valida, llama a la API y, si la API marca un campo (`errors[].field`), lo pinta en él; si no, aviso breve. */
    const enviar = (campos: readonly string[], accion: () => Promise<void>, deCampo: Readonly<Record<string, string>> = {}) => {
        setIntentado(true);
        setErrorCampo({});
        if (campos.some((k) => (errores[k] ?? '') !== '')) {
            return;
        }
        setOcupado(true);
        accion()
            .catch((e: unknown) => {
                if (e instanceof ErrorApi) {
                    const marcados: Record<string, string> = {};
                    for (const er of e.errores) {
                        const propio = deCampo[er.field];
                        if (propio !== undefined) {
                            marcados[propio] = propio === 'actual' ? ERRORES.actualMal : (er.message ?? e.problema.detail ?? e.problema.title);
                        }
                    }
                    const delCodigo = deCampo.code;
                    if (Object.keys(marcados).length === 0 && e.codigo === 'code_invalid' && delCodigo !== undefined) {
                        marcados[delCodigo] = e.problema.detail ?? e.problema.title;
                    }
                    if (Object.keys(marcados).length > 0) {
                        setErrorCampo(marcados);
                        return;
                    }
                }
                fallo(e);
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const hecho = (texto: string) => {
        avisar({ tipo: 'ok', texto });
        setModal(null);
    };

    const empezarAlta = () => {
        abrir('activar');
        setAlta(null);
        ops.startTwoFactorEnrollment()
            .then(setAlta)
            .catch((e: unknown) => {
                setModal(null);
                fallo(e);
            });
    };

    const tienePassword = formas?.has_password ?? cuenta.has_password ?? true;
    const dosPasos = formas?.two_factor_enabled ?? cuenta.two_factor_enabled;
    const miembro = membresias.find((m) => m.business_id === negocio) ?? membresia;
    const bloqueadoHasta = miembro?.pin_locked_until != null && new Date(miembro.pin_locked_until) > new Date() ? miembro.pin_locked_until : null;
    const tienePin = miembro?.has_pin === true;
    const tablets = miembro?.tablets ?? [];
    const otras = (sesiones ?? []).filter((s) => !s.current);
    const otrosDispositivos = otras.map(dispositivo);

    // ---- Contenido de cada apartado ----
    let filas: ReactNode = null;
    let pie: string | null = null;
    switch (apartado) {
        case 'perfil':
            filas = (
                <>
                    <Fila etiqueta="Nombre y apellidos" valor={cuenta.display_name} botones={[{ texto: 'Cambiar', icono: 'pencil', onClick: () => { abrir('nombre', { nombre: cuenta.display_name }); } }]} />
                    <Fila etiqueta="Email" valor={cuenta.email} botones={[{ texto: 'Cambiar', icono: 'pencil', onClick: () => { abrir('email'); } }]} />
                    {cuenta.pending_email != null && (
                        <Fila
                            etiqueta="Email nuevo"
                            valor={cuenta.pending_email}
                            sub={`Te hemos mandado un enlace y avisado a ${cuenta.email}. Sigues entrando con el actual hasta que lo abras.`}
                            chip={{ texto: 'Pendiente', estado: 'pending' }}
                            botones={[
                                {
                                    texto: 'Reenviar',
                                    icono: 'send',
                                    onClick: () => {
                                        // G211: el enlace nuevo va al email pendiente.
                                        ops.sendEmailVerification()
                                            .then(() => {
                                                avisar({ tipo: 'ok', texto: `Te hemos mandado otro enlace a ${cuenta.pending_email ?? ''}.` });
                                            })
                                            .catch(fallo);
                                    },
                                },
                                {
                                    texto: 'Anular',
                                    icono: 'x',
                                    rojo: true,
                                    onClick: () => {
                                        // G233: el enlace ya mandado deja de servir.
                                        ops.cancelEmailChange()
                                            .then(() => {
                                                avisar({ tipo: 'ok', texto: 'Cambio de email anulado.' });
                                                setCuenta((c) => ({ ...c, pending_email: null }));
                                                releer();
                                            })
                                            .catch(fallo);
                                    },
                                },
                            ]}
                        />
                    )}
                    <Fila
                        etiqueta="Idioma"
                        valor="Del panel y de tus emails"
                        extra={
                            <label className="rv-cuenta__select">
                                <Icono nombre="languages" />
                                <select
                                    aria-label="Idioma"
                                    value={cuenta.locale ?? 'es-ES'}
                                    onChange={(e) => {
                                        ops.updateMe({ locale: e.target.value })
                                            .then((c) => {
                                                setCuenta(c);
                                                avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
                                            })
                                            .catch(fallo);
                                    }}
                                >
                                    {IDIOMAS.map(([valor, texto]) => (
                                        <option key={valor} value={valor}>
                                            {texto}
                                        </option>
                                    ))}
                                </select>
                                <span>{IDIOMAS.find(([valor]) => valor === (cuenta.locale ?? 'es-ES'))?.[1] ?? cuenta.locale}</span>
                                <Icono nombre="chevron-down" />
                            </label>
                        }
                    />
                    {membresias.length > 0 && <Fila etiqueta="Negocios donde trabajas" valor={unir(membresias.map((m) => m.business_name))} />}
                </>
            );
            pie = PIE.perfil;
            break;
        case 'entrar': {
            const proveedores = formas?.providers ?? [];
            const unica = proveedores.length + (tienePassword ? 1 : 0) <= 1;
            filas = (
                <>
                    {tienePassword ? (
                        <Fila
                            icono="key-round"
                            etiqueta="Contraseña"
                            valor={cuenta.password_changed_at != null ? `Cambiada el ${fechaLarga(cuenta.password_changed_at)}` : 'Con contraseña'}
                            botones={[{ texto: 'Cambiar', icono: 'key-round', onClick: () => { abrir('contrasena'); } }]}
                        />
                    ) : (
                        <Fila
                            icono="key-round"
                            etiqueta="Contraseña"
                            valor="Sin contraseña"
                            sub={`Entras con ${unir(proveedores.map((p) => PROVEEDOR[p.provider] ?? p.provider))}. Crea una para entrar también con tu email.`}
                            botones={[{ texto: 'Crear', icono: 'plus', onClick: () => { abrir('crearContrasena'); } }]}
                        />
                    )}
                    {proveedores.map((p) => (
                        <Fila
                            key={p.provider}
                            icono="link"
                            etiqueta={PROVEEDOR[p.provider] ?? p.provider}
                            valor={p.email}
                            sub={
                                (p.relay_email_disabled === true
                                    ? 'Has desactivado en Apple el reenvío de tu correo oculto: no te llegan nuestros correos. Actívalo en tu cuenta de Apple. '
                                    : p.private_relay_email
                                      ? 'Correo oculto de Apple: te reenvía nuestros correos. '
                                      : '') +
                                `Vinculada el ${fechaLarga(p.linked_at)}` +
                                (p.last_used_at != null ? ` · Último uso: ${haceCuanto(p.last_used_at)}` : ' · Sin usar todavía') +
                                (unica ? '. Es tu única forma de entrar: crea una contraseña antes de quitarla.' : '.')
                            }
                            chip={p.relay_email_disabled === true ? { texto: 'Sin reenvío', estado: 'problem' } : null}
                        />
                    ))}
                </>
            );
            pie = PIE.entrar;
            break;
        }
        case 'seguridad':
            filas = !tienePassword ? (
                <Fila etiqueta="Verificación en dos pasos" valor="Sin activar" sub="Se pide al entrar con contraseña. Crea antes una contraseña en «Formas de entrar»." />
            ) : (
                <>
                    <Fila
                        etiqueta="Verificación en dos pasos"
                        valor={dosPasos ? 'Activa con aplicación de autenticación' : 'Sin activar'}
                        botones={
                            !dosPasos
                                ? [{ texto: 'Activar', icono: 'shield-check', onClick: empezarAlta }]
                                : cuenta.two_factor_required === true
                                  ? [] // Las cuentas de plataforma la tienen siempre (G65).
                                  : [{ texto: 'Quitar', icono: 'shield-off', rojo: true, onClick: () => { abrir('quitar2'); } }]
                        }
                    />
                    {dosPasos && (
                        <Fila
                            etiqueta="Códigos de recuperación"
                            valor={`Quedan ${String(cuenta.recovery_codes_left ?? 0)} códigos de recuperación`}
                            botones={[{ texto: 'Generar nuevos', icono: 'rotate-ccw', onClick: () => { abrir('regenerar'); } }]}
                        />
                    )}
                </>
            );
            break;
        case 'sesiones':
            filas = (
                <>
                    {(sesiones ?? []).map((s) => (
                        <Fila
                            key={s.id}
                            fuerte
                            icono={esMovil(s) ? 'smartphone' : 'laptop'}
                            etiqueta={dispositivo(s)}
                            valor={`${haceCuanto(s.last_used_at)} · ${METODO[s.sign_in_method ?? 'password'] ?? ''}`}
                            chip={s.current ? { texto: 'Este dispositivo', estado: 'neutral' } : null}
                            botones={
                                s.current
                                    ? []
                                    : [
                                          {
                                              texto: 'Cerrar',
                                              icono: 'log-out',
                                              rojo: true,
                                              onClick: () => {
                                                  ops.revokeMySession(s.id)
                                                      .then(() => {
                                                          setSesiones((l) => (l ?? []).filter((x) => x.id !== s.id));
                                                      })
                                                      .catch(fallo);
                                              },
                                          },
                                      ]
                            }
                        />
                    ))}
                    {otras.length > 0 && (
                        <Fila
                            fuerte
                            etiqueta="Cerrar todas las demás sesiones"
                            valor=""
                            sub="Por ejemplo, si has perdido un móvil. Esta sesión sigue abierta."
                            botones={[{ texto: 'Cerrar las demás', icono: 'log-out', rojo: true, onClick: () => { abrir('cerrarDemas'); } }]}
                        />
                    )}
                </>
            );
            if (sesiones !== null && otras.length === 0) {
                pie = PIE.soloEsta;
            }
            break;
        case 'pin':
            filas = (
                <>
                <Fila
                    etiqueta={`PIN de ${nombreNegocio}`}
                    valor={bloqueadoHasta !== null ? `Tu PIN está bloqueado hasta las ${hora(bloqueadoHasta, miembro?.timezone)}` : tienePin ? 'Elegido' : 'Sin elegir'}
                    sub={
                        bloqueadoHasta !== null
                            ? 'Si eliges uno nuevo, se desbloquea al momento.'
                            : tienePin
                              ? null
                              : 'Elige uno para entrar en las tablets. Si te lo han borrado desde Equipo, te hemos avisado por email.'
                    }
                    chip={bloqueadoHasta !== null ? { texto: 'Bloqueado', estado: 'problem' } : tienePin ? null : { texto: 'Pendiente', estado: 'pending' }}
                    botones={[{ texto: bloqueadoHasta !== null ? 'Elegir un PIN nuevo' : tienePin ? 'Cambiar' : 'Elegir', icono: 'key-round', onClick: () => { abrir('pin'); } }]}
                />
                {/* G337: las tablets activas de este negocio donde entra con su PIN (vacía sin tablets o sin el bloque). */}
                <Fila etiqueta="Tablets donde puedes entrar" valor={tablets.length === 0 ? 'Ninguna todavía' : unir(tablets.map((t) => t.name))} />
                </>
            );
            pie = PIE.pin;
            break;
    }

    const codigo = (autoFocus: boolean) => (
        <Campo etiqueta="Código de 6 números" valor={campo('codigo')} onChange={pon('codigo', cifras(6))} modo="numeric" autocompletar="one-time-code" max={6} error={err('codigo')} autoFocus={autoFocus} />
    );

    const ventanas: Record<Modal, DefVentana> = {
        nombre: {
            titulo: 'Cambiar el nombre',
            texto: 'Así te ve el resto del equipo en todos tus negocios.',
            accion: 'Guardar',
            icono: 'check',
            onAccion: () => {
                enviar(['nombre'], async () => {
                    setCuenta(await ops.updateMe({ display_name: campo('nombre').trim() }));
                    hecho('Cambios guardados.');
                    recargar();
                }, { display_name: 'nombre' });
            },
            cuerpo: <Campo etiqueta="Nombre y apellidos" valor={campo('nombre')} onChange={pon('nombre')} error={err('nombre')} autocompletar="name" max={120} autoFocus />,
        },
        email: {
            titulo: 'Cambiar el email',
            texto: `Te mandamos un enlace al email nuevo y avisamos a ${cuenta.email}. Sigues entrando con el actual hasta que abras el enlace.`,
            accion: 'Enviar el enlace',
            icono: 'send',
            onAccion: () => {
                enviar(['email'], async () => {
                    const nuevo = campo('email').trim();
                    await ops.startEmailChange(nuevo);
                    setCuenta((c) => ({ ...c, pending_email: nuevo }));
                    hecho(`Te hemos mandado un enlace a ${nuevo}.`);
                    releer();
                }, { email: 'email' });
            },
            cuerpo: <Campo etiqueta="Email nuevo" tipo="email" valor={campo('email')} onChange={pon('email')} error={err('email')} autoFocus />,
        },
        contrasena: {
            titulo: 'Cambiar la contraseña',
            texto: 'Al cambiarla se cierran tus demás sesiones.',
            accion: 'Cambiar la contraseña',
            icono: 'key-round',
            onAccion: () => {
                enviar(['actual', 'nueva', 'repetida'], async () => {
                    await ops.changePassword(campo('actual'), campo('nueva'));
                    hecho('Contraseña cambiada.');
                    releer();
                }, { current_password: 'actual', new_password: 'nueva' });
            },
            cuerpo: (
                <>
                    <Campo etiqueta="Contraseña actual" tipo="password" valor={campo('actual')} onChange={pon('actual')} error={err('actual')} autocompletar="current-password" />
                    <Campo etiqueta="Contraseña nueva" tipo="password" valor={campo('nueva')} onChange={pon('nueva')} error={err('nueva')} ayuda="Mínimo 12 caracteres." autocompletar="new-password" />
                    <Campo etiqueta="Repite la nueva" tipo="password" valor={campo('repetida')} onChange={pon('repetida')} error={err('repetida')} autocompletar="new-password" />
                </>
            ),
        },
        crearContrasena: {
            titulo: 'Crear una contraseña',
            texto: 'Para entrar también con tu email. Te avisaremos por email.',
            accion: 'Crear la contraseña',
            icono: 'key-round',
            onAccion: () => {
                enviar(['nueva', 'repetida'], async () => {
                    await ops.setMyPassword(campo('nueva'));
                    avisar({ tipo: 'ok', texto: 'Contraseña creada.' });
                    setFormas((x) => (x === null ? x : { ...x, has_password: true }));
                    releer();
                    // G117: con la contraseña nueva se ofrecen los dos pasos, sin obligar.
                    if (dosPasos) {
                        setModal(null);
                    } else {
                        abrir('ofrecer');
                    }
                }, { new_password: 'nueva' });
            },
            cuerpo: (
                <>
                    <Campo etiqueta="Contraseña" tipo="password" valor={campo('nueva')} onChange={pon('nueva')} error={err('nueva')} ayuda="Mínimo 12 caracteres. Comprobamos que no aparezca en filtraciones." autocompletar="new-password" />
                    <Campo etiqueta="Repítela" tipo="password" valor={campo('repetida')} onChange={pon('repetida')} error={err('repetida')} autocompletar="new-password" />
                </>
            ),
        },
        ofrecer: {
            titulo: '¿Activas los dos pasos?',
            texto: 'Al entrar con tu contraseña te pediremos también un código del móvil. Con Google o Apple no se pide. Es opcional.',
            accion: 'Activarla ahora',
            icono: 'shield-check',
            cancelar: 'Ahora no',
            onAccion: empezarAlta,
        },
        activar: {
            titulo: 'Activar los dos pasos',
            texto: 'Escanea el código con tu aplicación de autenticación y escribe los 6 números que te muestra.',
            accion: 'Activar',
            icono: 'shield-check',
            onAccion: () => {
                enviar(['codigo'], async () => {
                    const r = await ops.confirmTwoFactorEnrollment(campo('codigo'));
                    setCodigos(r.recovery_codes);
                    setFormas((x) => (x === null ? x : { ...x, two_factor_enabled: true }));
                    setCuenta((c) => ({ ...c, two_factor_enabled: true, recovery_codes_left: r.recovery_codes.length }));
                    releer();
                    abrir('codigos');
                }, { code: 'codigo' });
            },
            cuerpo: (
                <>
                    {alta === null ? (
                        <span className="rv-cuenta__nota">Preparando…</span>
                    ) : (
                        <div className="rv-cuenta__alta">
                            <CodigoQr valor={alta.otpauth_uri} etiqueta="Código QR para tu aplicación de autenticación" />
                            <a className="rv-cuenta__enlace" href={alta.otpauth_uri}>
                                <Icono nombre="smartphone" />
                                <span>Abrir en tu aplicación</span>
                            </a>
                            <button type="button" className="rv-cuenta__enlace" onClick={() => { setVerClave((v) => !v); }}>
                                <Icono nombre="key-round" />
                                <span>{verClave ? 'Ocultar la clave' : 'Ver la clave'}</span>
                            </button>
                            {verClave && <code className="rv-cuenta__clave">{alta.secret.replace(/(.{4})/g, '$1 ').trim()}</code>}
                        </div>
                    )}
                    {codigo(false)}
                </>
            ),
        },
        quitar2: {
            titulo: 'Quitar los dos pasos',
            texto: 'Tu cuenta quedará protegida solo con la contraseña. Para confirmarlo, escribe un código de tu aplicación.',
            accion: 'Quitar los dos pasos',
            icono: 'shield-off',
            onAccion: () => {
                enviar(['codigo'], async () => {
                    await ops.disableTwoFactor(campo('codigo'));
                    setFormas((x) => (x === null ? x : { ...x, two_factor_enabled: false }));
                    setCuenta((c) => ({ ...c, two_factor_enabled: false }));
                    hecho('Verificación en dos pasos quitada.');
                    releer();
                }, { code: 'codigo' });
            },
            cuerpo: codigo(true),
        },
        regenerar: {
            titulo: 'Generar códigos nuevos',
            texto: `Los ${String(cuenta.recovery_codes_left ?? 0)} códigos que te quedan dejarán de servir. Para confirmarlo, escribe un código de tu aplicación.`,
            accion: 'Generar',
            icono: 'rotate-ccw',
            onAccion: () => {
                enviar(['codigo'], async () => {
                    const r = await ops.regenerateRecoveryCodes(campo('codigo'));
                    setCodigos(r.recovery_codes);
                    setCuenta((c) => ({ ...c, recovery_codes_left: r.recovery_codes.length }));
                    releer();
                    abrir('codigos');
                }, { code: 'codigo' });
            },
            cuerpo: codigo(true),
        },
        codigos: {
            titulo: 'Tus códigos de recuperación',
            texto: 'Guárdalos en un sitio seguro. Cada uno sirve una vez y no los volverás a ver.',
            accion: 'Ya los he guardado',
            icono: 'check',
            cancelar: '',
            onAccion: cerrar,
            cuerpo: (
                <>
                    <ol className="rv-cuenta__codigos" aria-label="Códigos de recuperación">
                        {codigos.map((c) => (
                            <li key={c}>{c}</li>
                        ))}
                    </ol>
                    <button
                        type="button"
                        className="rv-cuenta__enlace"
                        onClick={() => {
                            navigator.clipboard
                                .writeText(codigos.join('\n'))
                                .then(() => {
                                    avisar({ tipo: 'ok', texto: 'Copiados.' });
                                })
                                .catch(() => undefined);
                        }}
                    >
                        <Icono nombre="copy" />
                        <span>Copiar</span>
                    </button>
                </>
            ),
        },
        pin: {
            titulo: tienePin ? 'Cambiar el PIN' : 'Elegir el PIN',
            texto: `De 4 a 6 cifras para entrar en las tablets de ${nombreNegocio}. No uses tu fecha de nacimiento.`,
            accion: 'Guardar',
            icono: 'check',
            onAccion: () => {
                enviar(['pin', 'pin2'], async () => {
                    await ops.setMyPin(negocio, campo('pin'));
                    setMembresias((l) => l.map((m) => (m.business_id === negocio ? { ...m, has_pin: true, pin_locked_until: null } : m)));
                    hecho('PIN guardado.');
                    releer();
                    recargar();
                }, { pin: 'pin' });
            },
            cuerpo: (
                <>
                    <Campo etiqueta="PIN" tipo="password" valor={campo('pin')} onChange={pon('pin', cifras(6))} modo="numeric" autocompletar="off" max={6} ayuda="De 4 a 6 cifras." error={err('pin')} autoFocus />
                    <Campo etiqueta="Repite el PIN" tipo="password" valor={campo('pin2')} onChange={pon('pin2', cifras(6))} modo="numeric" autocompletar="off" max={6} error={err('pin2')} />
                </>
            ),
        },
        cerrarDemas: {
            titulo: 'Cerrar las demás sesiones',
            texto: `Saldrás de ${otrosDispositivos.length === 1 ? 'un dispositivo' : `${String(otrosDispositivos.length)} dispositivos`} (${unir(otrosDispositivos)}) y tendrás que volver a entrar en ${otrosDispositivos.length === 1 ? 'él' : 'ellos'}. Esta sesión sigue abierta.`,
            accion: 'Cerrar sesiones',
            icono: 'log-out',
            onAccion: () => {
                enviar([], async () => {
                    await ops.revokeOtherSessions();
                    setSesiones((l) => (l ?? []).filter((s) => s.current));
                    hecho('Sesiones cerradas.');
                });
            },
        },
    };
    const v = modal === null ? null : ventanas[modal];

    return (
        <div className="rv-cuenta">
            <div className="rv-cuenta__barra">
                <Segmentos
                    etiqueta="Apartados de tu cuenta"
                    movil={movil}
                    items={apartados.map((a) => ({ etiqueta: a.etiqueta, corta: a.etiqueta, icono: a.icono }))}
                    valor={Math.max(0, apartados.findIndex((a) => a.clave === apartado))}
                    onCambio={(i) => {
                        const a = apartados[i];
                        if (a !== undefined) {
                            navegar(rutaConApartado(ruta, a.clave === 'perfil' ? null : a.clave), true);
                        }
                    }}
                />
            </div>
            <div key={apartado} className="rv-cuenta__tarjeta">
                {filas}
            </div>
            {pie !== null && <p className="rv-cuenta__pie">{pie}</p>}
            <Ventana
                abierta={v !== null}
                titulo={v?.titulo ?? ''}
                texto={v?.texto}
                accion={v?.accion}
                accionIcono={v?.icono}
                cancelar={v?.cancelar ?? 'Cancelar'}
                ocupado={ocupado}
                ocupadoTexto="Un momento…"
                movil={movil}
                onAccion={v?.onAccion}
                onCerrar={cerrar}
            >
                {v?.cuerpo}
            </Ventana>
        </div>
    );
}
